import { Injectable } from '@angular/core'
import {
  CanActivate, Router,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  CanActivateChild,
} from '@angular/router'
import { NuclearObservable } from '../nuclear-observable'
import { Account, Customer } from '../types'
import { customerGetters } from '../stores/customer-store'
import { CustomerActions } from '../actions/customer-actions'
import { AccountActions } from '../actions/account-actions'
import { earliestUnfinishedRoute } from '../logic/customer-signup'
import { present } from '../utils'
import isEmpty from 'lodash-es/isEmpty'

@Injectable()
export class EarliestUnfinishedRouteGuard implements CanActivate, CanActivateChild {
  account: Account;
  customer: Customer;
  customerId: number;

  constructor(private router: Router,
  private customerActions: CustomerActions,
  private accountActions: AccountActions
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    this.customerId = +route.params['customerId']
    return this.checkEarliestUnfinishedRoute(route)
  }

  canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    return this.canActivate(route, state)
  }

  checkEarliestUnfinishedRoute(route: ActivatedRouteSnapshot): Promise<boolean> {
    const navigate = (r: any[]): void => { // pass query params through redirect
      this.router.navigate(r, { queryParams: route.queryParams })
    }

    return new Promise((resolve, reject) => {
      this.account = this.getAccount()
      if (this.customerId) {
        this.customer = this.getCustomer(this.customerId)
        // Don't need to fetch customer or account
        if (present(this.customer) && present(this.account)) {
          navigate(earliestUnfinishedRoute(this.account, this.customer))
          resolve(false)
        }

        // Need to fetch customer only
        else if (isEmpty(this.customer) && present(this.account)) {
          this.customerActions.loadCustomer(this.customerId).then(() => {
            this.customer = this.getCustomer(this.customerId)
            navigate(earliestUnfinishedRoute(this.account, this.customer))
            resolve(false)
          })
        }

        // Need to fetch account only
        else if (present(this.customer) && isEmpty(this.account)) {
          this.accountActions.loadAccount().then(() => {
            this.account = this.getAccount()
            navigate(earliestUnfinishedRoute(this.account, this.customer))
            resolve(false)
          })
        }

        // Need to fetch customer and account
        else {
          this.accountActions.loadAccount().then(() => {
            this.account = this.getAccount()
            this.customerActions.loadCustomer(this.customerId).then(() => {
              this.customer = this.getCustomer(this.customerId)
              navigate(earliestUnfinishedRoute(this.account, this.customer))
              resolve(false)
            })
          })
        }
      }

      // Need to fetch "firstCustomer"
      else {
        this.customerActions.loadCustomers().then(() => {
          const customers = this.getCustomers()
          this.customer = customers[0]
          if (present(this.account)) {
            navigate(earliestUnfinishedRoute(this.account, this.customer))
            resolve(false)
          } else {
            this.accountActions.loadAccount().then(() => {
              this.account = this.getAccount()
              navigate(earliestUnfinishedRoute(this.account, this.customer))
              resolve(false)
            })
          }
        })
      }
    })
  }

  private getAccount(): Account {
    return NuclearObservable.currentValue<Account>(customerGetters.account, { toJS: true })
  }

  private getCustomer(id: number): Customer {
    return NuclearObservable.currentValue<Customer>(customerGetters.customer(id), { toJS: true })
  }

  private getCustomers(): Customer[] {
    return NuclearObservable.currentValue<Customer[]>(customerGetters.shallowCustomers, { toJS: true })
  }

}
